import { Injectable } from '@angular/core';
import {
  type ColorViewAssignment,
  type ColorViewParams,
  type ColorViewStyles,
} from '@bizzdesign/modeler/canvazz/types';
import {
  type ColorCodeConfig,
  type FillDefinition,
  type GenericObject,
  type StrokeDefinition,
} from '@bizzdesign/modeler/models';
import {
  colorHexToRgb,
  colorRgbToHex,
  generateBorderColorViaHSB,
  getColorPriorityLevelForAttribute,
  hasPromotionSemanticsIdByGraphicId,
  isColorCodeNoValueCode,
} from '@bizzdesign/modeler/utils-modeler';
import {
  type AttributeRangeValue,
  type AttributeValueColorMap,
  type ColorCodeType,
  type ColorValuesMap,
  colorLegendNoValueKey,
} from '@bizzdesign/shared/models';

@Injectable({
  providedIn: 'root',
})
export class ModelerColorsService {
  //! N.B: color codes used in ColorViewAssignment and ColorViewStyles should match!
  // N.B: all methods support only 1 color per object

  buildValueColorCodeMap(
    ranges: AttributeRangeValue[],
    onlyColorCodesForValues: ColorCodeConfig[],
  ): Record<string, ColorCodeType> {
    const maximumPrimaryColors = onlyColorCodesForValues.filter(
      (codeObj) => codeObj.priority === 1,
    ).length;

    // the || is in case the ranges are less than number of primary colors
    const levelsNeeded = Math.ceil(ranges.length / maximumPrimaryColors) || 1;

    const maxPriority = Math.max(
      ...onlyColorCodesForValues.map((c) => c.priority),
    );

    // generate an array for maximum index ranges for each priority level
    const levelArray = Array.from(
      { length: levelsNeeded },
      (_, i) => i + 1,
    ).map((value) => value * maximumPrimaryColors);
    return ranges.reduce<Record<string, ColorCodeType>>(
      (acc, rangeObj, index) => {
        const colorIndex = index % maximumPrimaryColors;
        const colorPriority = Math.min(
          maxPriority,
          getColorPriorityLevelForAttribute(index, levelArray),
        );

        return {
          ...acc,
          [rangeObj.value.toString()]:
            rangeObj.value === colorLegendNoValueKey
              ? 'grey'
              : onlyColorCodesForValues.filter(
                  (codeObj) => codeObj.priority === colorPriority,
                )[colorIndex].code,
        };
      },
      {},
    );
  }

  /**
   * Creates a map of graphicalIds: colorCodeString.
   * @param objects Array of objects from the modeler
   * @param ranges Array of objects that hold attribute values + graphicIds array
   * @param colorCodes Array of color codes like 'white', 'red_1', 'green_2' etc
   * @param isRandom flag for testing purposes, ignores attribute values
   * @returns ColorViewAssignment object or undefined if passed an empty arrays for data
   */
  buildColorViewAssignmentMap(
    objects: GenericObject[],
    ranges: AttributeRangeValue[],
    colorCodes: readonly ColorCodeConfig[],
    isRandom = false,
  ): ColorViewAssignment | undefined {
    const ids = objects.map((object) => object['id']);
    const onlyColorCodesForValues = colorCodes.filter(
      (codeObject) => !isColorCodeNoValueCode(codeObject.code),
    );

    if (!ids.length) return undefined;

    let idColorsMap: ColorViewAssignment = {};

    if (isRandom) {
      // randomly assign colors. Meant for tests only
      idColorsMap = this.generateRandomIdColorMap(
        objects,
        ids,
        onlyColorCodesForValues,
      );
    } else {
      const valueColorCodeMap = this.buildValueColorCodeMap(
        ranges,
        onlyColorCodesForValues,
      );

      idColorsMap = this.generateIdColorMap(
        objects,
        ranges,
        ids,
        valueColorCodeMap,
      );
    }

    return idColorsMap;
  }

  /**
   * Creates a map of color styles for each element in the colorCodes array.
   * The styles matches the styles definition in the modeler library. The styles definition accepts an array
   * for the fill, in case an object's code needs to display multiple colors. Currently it only generates 1 fill
   * definition per color code.
   * @param colorCodes an array of configs for color codes. The `code` attributes are used as a keys in colorValuesMap
   * @param colorValuesMap a map of color codes and color values. Values can be RGB objects or HEX strings
   * @returns ColorViewStyles object which includes style definitions for each color code. If an empty colorCodes array
   */
  getColorStylesMap(
    colorCodes: readonly ColorCodeConfig[],
    colorValuesMap: ColorValuesMap,
  ): ColorViewStyles | undefined {
    if (!colorCodes.length) return undefined;

    const colorViewStyles = colorCodes.reduce<ColorViewStyles>(
      (accumulator, codeObj) => {
        const code = codeObj.code;
        const colorValue = colorHexToRgb(colorValuesMap[code].color);

        // opacity value is defined like in styles for shape types (refer to ShapeTypes.ts in modeler lib)
        const fillDefinition: FillDefinition = {
          color: colorValue,
          opacity: 255,
        };
        // we're creating a darker version of the color for the stroke
        // the stroke is primarily used in the border and icon border
        const strokeColor = generateBorderColorViaHSB(colorValue, 1);
        const strokeDefinition: StrokeDefinition = { color: strokeColor };
        accumulator = {
          ...accumulator,
          [code]: {
            fill: [fillDefinition],
            stroke: strokeDefinition,
          },
        };
        return accumulator;
      },
      {},
    );
    return colorViewStyles;
  }

  /**
   * Create color view parameters from modeler data. When not using mocks,
   * the same array of color codes should be passed to both buildColorViewAssignmentMap and getColorStylesMap
   * @param modelData array of elements from the modeler
   * @param colorCodes array of color codes to use
   * @param colorValues map of color values for passed color codes to use
   * @param isRandom flag to trigger use of mock color codes and color values
   * @returns ColorViewParams that include a map of the color codes for graphicIds and color values for codes.
   * Returns undefined if neither one is generated.
   */
  getColorViewDataForModelData(
    modelData: GenericObject[],
    colorCodes: readonly ColorCodeConfig[],
    colorValues: ColorValuesMap,
    attributeRanges: AttributeRangeValue[] = [],
    isRandom?: boolean,
  ): ColorViewParams | undefined {
    const objects = modelData.filter(
      (element) => !element['sourceId'] && element['elementKind'] === 'diagram',
    );
    const colorViewMap = this.buildColorViewAssignmentMap(
      objects,
      attributeRanges,
      colorCodes,
      !!isRandom,
    );

    const colorViewStyles = this.getColorStylesMap(colorCodes, colorValues);

    if (!!!colorViewStyles || !!!colorViewMap) return;

    return {
      colorViewMap,
      colorViewStyles,
    };
  }

  getLegendColorsForAttributeValues(
    ranges: AttributeRangeValue[],
    colorCodes: ColorCodeConfig[],
    colorViewStyles: ColorViewStyles,
  ): AttributeValueColorMap {
    const onlyColorCodesForValues = colorCodes.filter(
      (codeObject) => !isColorCodeNoValueCode(codeObject.code),
    );
    const valueColorCodeMap = this.buildValueColorCodeMap(
      ranges,
      onlyColorCodesForValues,
    );
    return Object.keys(valueColorCodeMap).reduce<AttributeValueColorMap>(
      (acc, key) => {
        const code = valueColorCodeMap[key];
        const rgbColor = colorViewStyles[code].fill?.[0].color ?? {
          r: 255,
          g: 255,
          b: 255,
        };
        const hexColor = colorRgbToHex(rgbColor, true);
        return {
          ...acc,
          [key]: { color: hexColor, type: code },
        };
      },
      {} as AttributeValueColorMap,
    );
  }

  generateRandomIdColorMap(
    objects: GenericObject[],
    ids: string[],
    colorCodesForValues: ColorCodeConfig[],
  ): ColorViewAssignment {
    return ids.reduce<ColorViewAssignment>((acc, id) => {
      const hasSemantics = hasPromotionSemanticsIdByGraphicId(objects, id);

      acc = {
        ...acc,
        [id]: hasSemantics
          ? colorCodesForValues[
              Math.floor(Math.random() * colorCodesForValues.length)
            ].code
          : 'white',
      };

      return acc;
    }, {} as ColorViewAssignment);
  }

  generateIdColorMap(
    objects: GenericObject[],
    ranges: AttributeRangeValue[],
    ids: string[],
    valueColorCodeMap: Record<string, ColorCodeType>,
  ): ColorViewAssignment {
    return ids.reduce<ColorViewAssignment>((acc, id) => {
      let colorCode: ColorCodeType = 'white';

      const hasSemanticsOrIsFromRepo = !!objects.some(
        (obj) => obj['id'] === id || obj['semanticsId'] === id,
      );

      // Only apply colors if the element hasSemanticId or is from repo
      if (hasSemanticsOrIsFromRepo) {
        const attributeValueObj = ranges.find((rangeObj) =>
          rangeObj.graphicIds.includes(id),
        );

        if (attributeValueObj?.value === colorLegendNoValueKey) {
          colorCode = 'grey';
        } else {
          colorCode =
            valueColorCodeMap[
              attributeValueObj?.value.toString() ?? colorLegendNoValueKey
            ];
        }
      }

      acc = {
        ...acc,
        [id]: colorCode,
      };

      return acc;
    }, {} as ColorViewAssignment);
  }
}
